import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select, Label, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";

const FreePlanSurveyForm = ({ getSurveyResult, validate }) => {
  const { t } = useTranslation();
  const [surveyResults, setSurveyResult] = useState({
    firstQuestion: null,
    secondQuestion: null,
    thirdQuestion: null,
  });

  useEffect(() => {
    getSurveyResult(surveyResults);
    validate(
      surveyResults.firstQuestion !== null &&
        surveyResults.secondQuestion !== null &&
        surveyResults.thirdQuestion !== null
    );
  }, [getSurveyResult, surveyResults, validate]);

  let surveySelectQuestions = {
    firstQuestions: [
      { id: 1, name: "Google search" },
      { id: 2, name: "Social media (Facebook, Twitter, Linkedin…)" },
      { id: 3, name: "App listing website (Capterra, GetApp, G2…)" },
      { id: 4, name: "Recommendation" },
      { id: 16, name: "Blog or publication" },
      { id: 5, name: "Used it before" },
      { id: 6, name: "Other" },
    ],
    secondQuestion: [
      { id: 17, name: "Business owner" },
      { id: 18, name: "Business analyst" },
      { id: 7, name: "Project manager" },
      { id: 19, name: "Business consultant" },
      { id: 8, name: "Designer" },
      { id: 9, name: "Developer" },
      { id: 10, name: "Marketing" },
      { id: 11, name: "Other" },
    ],
    thirdQuestion: [
      { id: 12, name: "Notes" },
      { id: 13, name: "Spreadsheets" },
      { id: 14, name: "Another project management tool" },
      { id: 20, name: "Calendars and planners" },
      { id: 21, name: "Email" },
      { id: 15, name: "Other" },
    ],
  };

  const [firstAnswerSelectedOption, setFirstAnswerSelectedOption] = useState(null);
  const [secondAnswerSelectedOption, setSecondAnswerSelectedOption] = useState(null);
  const [thirdAnswerSelectedOption, setThirdAnswerSelectedOption] = useState(null);

  const handleSelectFirstAnswer = useCallback(
    (selectedAnswer) => {
      let answer = surveySelectQuestions.firstQuestions.find((answer) => answer.id === selectedAnswer);

      setSurveyResult({
        ...surveyResults,
        firstQuestion: answer.id,
      });
      setFirstAnswerSelectedOption(answer.id);
    },
    [surveyResults, surveySelectQuestions.firstQuestions]
  );

  const handleSelectSecondAnswer = useCallback(
    (selectedAnswer) => {
      let answer = surveySelectQuestions.secondQuestion.find((answer) => answer.id === selectedAnswer);

      setSurveyResult({
        ...surveyResults,
        secondQuestion: answer.id,
      });
      setSecondAnswerSelectedOption(answer.id);
    },
    [surveyResults, surveySelectQuestions.secondQuestion]
  );

  const handleSelectThirdAnswer = useCallback(
    (selectedAnswer) => {
      let answer = surveySelectQuestions.thirdQuestion.find((answer) => answer.id === selectedAnswer);

      setSurveyResult({
        ...surveyResults,
        thirdQuestion: answer.id,
      });
      setThirdAnswerSelectedOption(answer.id);
    },
    [surveyResults, surveySelectQuestions.thirdQuestion]
  );

  return (
    <>
      <div className="tw-mt-4 tw-mb-6">
        <Label
          htmlFor="source"
          className="tw-mb-1"
        >
          {t("How did you find out about ActiveCollab?")}
        </Label>
        <Select
          id="source"
          selected={firstAnswerSelectedOption}
          emptyAction={null}
          defaultValue={null}
          target={
            <SelectTrigger className="tw-text-primary tw-cursor-pointer">
              {surveyResults.firstQuestion !== null ? (
                surveySelectQuestions.firstQuestions.find((question) => question.id === surveyResults.firstQuestion)
                  .name
              ) : (
                <div data-testid="survey-first-question">Select answer...</div>
              )}
            </SelectTrigger>
          }
          disabledInternalSort
          onChange={handleSelectFirstAnswer}
          options={surveySelectQuestions.firstQuestions}
          type="single"
          mode="wider"
          forceCloseMenu
          disableSearch
        />
      </div>
      <div className="tw-mb-6">
        <Label
          htmlFor="role"
          className="tw-mb-1"
        >
          {t("Which role best describes you?")}
        </Label>
        <Select
          id="role"
          selected={secondAnswerSelectedOption}
          emptyAction={null}
          defaultValue={null}
          target={
            <SelectTrigger className="tw-text-primary tw-cursor-pointer">
              {surveyResults.secondQuestion !== null ? (
                surveySelectQuestions.secondQuestion.find((question) => question.id === surveyResults.secondQuestion)
                  .name
              ) : (
                <div>Select answer...</div>
              )}
            </SelectTrigger>
          }
          disabledInternalSort
          onChange={handleSelectSecondAnswer}
          options={surveySelectQuestions.secondQuestion}
          type="single"
          mode="wider"
          forceCloseMenu
          disableSearch
        />
      </div>
      <div>
        <Label
          htmlFor="use"
          className="tw-mb-1"
        >
          {t("What have you used so far to organize your work?")}
        </Label>
        <Select
          id="use"
          selected={thirdAnswerSelectedOption}
          emptyAction={null}
          defaultValue={null}
          target={
            <SelectTrigger className="tw-text-primary tw-cursor-pointer">
              {surveyResults.thirdQuestion !== null ? (
                surveySelectQuestions.thirdQuestion.find((question) => question.id === surveyResults.thirdQuestion).name
              ) : (
                <div>Select answer...</div>
              )}
            </SelectTrigger>
          }
          disabledInternalSort
          onChange={handleSelectThirdAnswer}
          options={surveySelectQuestions.thirdQuestion}
          type="single"
          mode="wider"
          forceCloseMenu
          disableSearch
        />
      </div>
    </>
  );
};

FreePlanSurveyForm.propTypes = {
  getSurveyResult: PropTypes.func,
  validate: PropTypes.func,
};

FreePlanSurveyForm.displayName = "FreePlanSurveyForm";

export default FreePlanSurveyForm;
